import { useMemo } from 'react'
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import * as Sentry from '@sentry/react'

export interface OnboardingState {
  // Core navigation state
  currentStep: number
  totalSteps: number
  stepTitles: string[]
  
  // User data being collected
  userType: string
  personalInfo: Record<string, unknown>
  businessInfo: Record<string, unknown>
  
  // Guards and watchdog state
  stepChangeTimestamps: number[]
  hasConsumedLocationState: boolean
  hasConsumedStepQuery: boolean
  walletAutoAdvanced: boolean
  
  // Actions
  setCurrentStep: (step: number, reason?: string) => void
  setStepTitles: (titles: string[]) => void
  updateUserData: (data: {
    userType?: string
    personalInfo?: Record<string, unknown>
    businessInfo?: Record<string, unknown>
  }) => void
  
  // Guards and navigation helpers
  recordStepChange: (from: number, to: number, reason: string) => boolean // Returns false if blocked by watchdog
  markLocationStateConsumed: () => void
  markStepQueryConsumed: () => void
  markWalletAutoAdvanced: () => void
  resetWalletAutoAdvanced: () => void
  canChangeStep: () => boolean
  
  // Reset/cleanup
  reset: () => void
}

const STEP_CHANGE_WINDOW_MS = 1500
const STEP_CHANGE_MAX_IN_WINDOW = 8

const initialState: Omit<OnboardingState, 
  | 'setCurrentStep' | 'setStepTitles' | 'updateUserData' | 'recordStepChange'
  | 'markLocationStateConsumed' | 'markStepQueryConsumed' | 'markWalletAutoAdvanced'
  | 'resetWalletAutoAdvanced' | 'canChangeStep' | 'reset'
> = {
  currentStep: 1,
  totalSteps: 0,
  stepTitles: [],
  userType: '',
  personalInfo: {},
  businessInfo: {},
  stepChangeTimestamps: [],
  hasConsumedLocationState: false,
  hasConsumedStepQuery: false,
  walletAutoAdvanced: false,
}

export const useOnboardingStore = create<OnboardingState>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    setCurrentStep: (step, reason = 'direct') => {
      const state = get()

      if (step === state.currentStep) {
        return
      }
      
      // Check watchdog before allowing step change
      if (!state.canChangeStep()) {
        try {
          Sentry.addBreadcrumb({
            category: 'onboarding.store',
            level: 'warning',
            message: 'Step change blocked by watchdog',
            data: { from: state.currentStep, to: step, reason }
          })
        } catch (_) { /* ignore */ }
        return
      }
      
      // Record the step change
      const blocked = !state.recordStepChange(state.currentStep, step, reason)
      if (blocked) {
        return
      }
      
      set({ currentStep: step })
    },

    setStepTitles: (titles) => {
      set((state) => {
        const sameLength = state.stepTitles.length === titles.length
        const sameContent = sameLength && state.stepTitles.every((value, index) => value === titles[index])
        if (sameContent && state.totalSteps === titles.length) {
          return state
        }
        return {
          stepTitles: titles,
          totalSteps: titles.length,
        }
      })
    },

    updateUserData: (data) => {
      set((state) => {
        const nextUserType = data.userType ?? state.userType
        const nextPersonalInfo = data.personalInfo
          ? { ...state.personalInfo, ...data.personalInfo }
          : state.personalInfo
        const nextBusinessInfo = data.businessInfo
          ? { ...state.businessInfo, ...data.businessInfo }
          : state.businessInfo

        const userTypeChanged = nextUserType !== state.userType
        const personalChanged = nextPersonalInfo !== state.personalInfo
        const businessChanged = nextBusinessInfo !== state.businessInfo

        if (!userTypeChanged && !personalChanged && !businessChanged) {
          return state
        }

        return {
          ...(userTypeChanged && { userType: nextUserType }),
          ...(personalChanged && { personalInfo: nextPersonalInfo }),
          ...(businessChanged && { businessInfo: nextBusinessInfo }),
        }
      })
    },

    recordStepChange: (from, to, reason) => {
      const now = Date.now()
      const state = get()
      const newTimestamps = [...state.stepChangeTimestamps, now]
        .filter(ts => now - ts <= STEP_CHANGE_WINDOW_MS)
      
      set({ stepChangeTimestamps: newTimestamps })
      
      // Check if we're exceeding the limit
      if (newTimestamps.length > STEP_CHANGE_MAX_IN_WINDOW) {
        try {
          Sentry.captureMessage('Onboarding step change watchdog triggered', {
            level: 'error',
            extra: { from, to, reason, count: newTimestamps.length }
          })
        } catch (_) { /* ignore */ }
        return false
      }
      
      return true
    },

    markLocationStateConsumed: () => {
      set({ hasConsumedLocationState: true })
    },

    markStepQueryConsumed: () => {
      set({ hasConsumedStepQuery: true })
    },

    markWalletAutoAdvanced: () => {
      set({ walletAutoAdvanced: true })
    },

    resetWalletAutoAdvanced: () => {
      set({ walletAutoAdvanced: false })
    },

    canChangeStep: () => {
      const state = get()
      const now = Date.now()
      const recentChanges = state.stepChangeTimestamps.filter(
        ts => now - ts <= STEP_CHANGE_WINDOW_MS
      )
      return recentChanges.length <= STEP_CHANGE_MAX_IN_WINDOW
    },

    reset: () => {
      set(initialState)
    },
  }))
)

// Selector hooks for common patterns
export const useCurrentStep = () => {
  return useOnboardingStore((state) => state.currentStep)
}

export const useStepTitles = () => {
  return useOnboardingStore((state) => state.stepTitles)
}

export const useOnboardingUserData = () => {
  const userType = useOnboardingStore((state) => state.userType)
  const personalInfo = useOnboardingStore((state) => state.personalInfo)
  const businessInfo = useOnboardingStore((state) => state.businessInfo)

  return useMemo(
    () => ({ userType, personalInfo, businessInfo }),
    [userType, personalInfo, businessInfo],
  )
}

export const useOnboardingActions = () => {
  const setCurrentStep = useOnboardingStore((state) => state.setCurrentStep)
  const setStepTitles = useOnboardingStore((state) => state.setStepTitles)
  const updateUserData = useOnboardingStore((state) => state.updateUserData)
  const markLocationStateConsumed = useOnboardingStore((state) => state.markLocationStateConsumed)
  const markStepQueryConsumed = useOnboardingStore((state) => state.markStepQueryConsumed)
  const markWalletAutoAdvanced = useOnboardingStore((state) => state.markWalletAutoAdvanced)
  const resetWalletAutoAdvanced = useOnboardingStore((state) => state.resetWalletAutoAdvanced)
  const reset = useOnboardingStore((state) => state.reset)

  return useMemo(
    () => ({
      setCurrentStep,
      setStepTitles,
      updateUserData,
      markLocationStateConsumed,
      markStepQueryConsumed,
      markWalletAutoAdvanced,
      resetWalletAutoAdvanced,
      reset,
    }),
    [
      setCurrentStep,
      setStepTitles,
      updateUserData,
      markLocationStateConsumed,
      markStepQueryConsumed,
      markWalletAutoAdvanced,
      resetWalletAutoAdvanced,
      reset,
    ],
  )
}